/** Page-number pagination controls for the standard DRF `{count, next,
 *  previous, results}` list envelope. Prev / next buttons + a numeric
 *  "from–to / count" range (numbers only, so no per-locale strings beyond
 *  the reused common.previous / common.next labels). Renders nothing when
 *  everything fits on one page. */
import { useTranslation } from "react-i18next";

type Props = {
  page: number;
  pageSize: number;
  count: number;
  onPage: (page: number) => void;
  disabled?: boolean;
};

export function Pagination({ page, pageSize, count, onPage, disabled }: Props) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  if (count <= pageSize) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, count);

  return (
    <div className="flex items-center justify-between gap-3 pt-3 text-sm">
      <span className="text-xs text-nb-text-3">
        {from.toLocaleString()}–{to.toLocaleString()} / {count.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-ghost text-xs"
          disabled={disabled || page <= 1}
          onClick={() => onPage(page - 1)}
        >
          {t("common.previous")}
        </button>
        <span className="text-xs text-nb-text-3">
          {page.toLocaleString()} / {totalPages.toLocaleString()}
        </span>
        <button
          type="button"
          className="btn-ghost text-xs"
          disabled={disabled || page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}
